import { useCallback, useEffect, useRef, useState } from 'react';
import { emptyDraft, type IntakeDraft, normalizeDraft } from './model';

const DRAFT_KEY = 'askarthur.storeOnboarding.draft.v1';

const keyFor = (scope: string) => `${DRAFT_KEY}.${scope}`;

export function readDraft(scope: string): IntakeDraft | null {
  try {
    const raw = sessionStorage.getItem(keyFor(scope));
    return raw ? normalizeDraft(JSON.parse(raw) as IntakeDraft) : null;
  } catch {
    return null;
  }
}

export function clearDraft(scope: string) {
  try {
    sessionStorage.removeItem(keyFor(scope));
  } catch {
    /* storage unavailable */
  }
}

const snapshotOf = (d: IntakeDraft) =>
  JSON.stringify({ ...d, updatedAt: null });

/**
 * The intake draft, saved to sessionStorage 400ms after the last change so a
 * refresh doesn't lose 60 fields. `scope` keeps activations apart. Nothing
 * is written (and `savedAt` stays null) until the draft differs from what
 * was loaded or seeded. `pause` holds that write — an address lookup in
 * flight — so a refresh can't store a suggestion with no street, city or
 * postal code. Files are not kept.
 */
export function useIntakeDraft(scope: string, pause = false) {
  const [initial] = useState(() => readDraft(scope));
  const [draft, setDraft] = useState<IntakeDraft>(
    () => initial ?? emptyDraft(),
  );
  const [savedAt, setSavedAt] = useState<number | null>(
    initial?.updatedAt ?? null,
  );
  const lastSaved = useRef(snapshotOf(draft));

  useEffect(() => {
    if (pause) return;
    const snapshot = snapshotOf(draft);
    if (snapshot === lastSaved.current) return;
    const t = window.setTimeout(() => {
      const now = Date.now();
      try {
        sessionStorage.setItem(
          keyFor(scope),
          JSON.stringify({ ...draft, updatedAt: now }),
        );
        lastSaved.current = snapshot;
        setSavedAt(now);
      } catch {
        /* quota: the form still works, it just won't survive a refresh */
      }
    }, 400);
    return () => window.clearTimeout(t);
  }, [draft, scope, pause]);

  const update = useCallback(
    (fn: (d: IntakeDraft) => IntakeDraft) => setDraft(fn),
    [],
  );

  const reset = useCallback(() => {
    clearDraft(scope);
    const fresh = emptyDraft();
    lastSaved.current = snapshotOf(fresh);
    setDraft(fresh);
    setSavedAt(null);
  }, [scope]);

  return { draft, update, reset, savedAt, restored: initial != null };
}
